import {
  useMutation,
  useQueryClient,
  type QueryKey,
  type UseMutationOptions,
  type UseMutationResult,
} from '@tanstack/react-query'

/**
 * Reusable optimistic-list-mutation helper.
 *
 * Most collaboration write paths follow the same shape:
 *  1. Run the mutation against the server.
 *  2. Pre-emptively apply the change to one cached list so the UI flips
 *     immediately.
 *  3. On error, rollback to the previous snapshot.
 *  4. On settle, invalidate so the server's authoritative shape replaces
 *     the optimistic one.
 *
 * Copy-pasting the snapshot/rollback/invalidate dance into every mutation
 * is how subtle bugs creep in (forgot to cancel in-flight refetches,
 * forgot to rollback in onError, …). This wrapper bakes the contract in.
 *
 * Pattern docs (see PR description for the long version):
 *
 *   const mutation = useOptimisticListMutation<WorkspaceRead, WorkspaceCreate>({
 *     mutationFn: async (body) => {
 *       const { data, error } = await apiClient.POST('/dashboard/workspaces', { body })
 *       if (error) throw new ApiError(error)
 *       return data
 *     },
 *     queryKey: queryKeys.workspaces.list(),
 *     applyOptimistic: (list, body) => [
 *       ...list,
 *       { ...newWorkspaceShape(body), uuid: tempUuid() },
 *     ],
 *     invalidateKeys: [queryKeys.workspaces.all],
 *   })
 *
 * The helper deliberately works on a single list cache. For more complex
 * cases (multi-list invalidation, item-detail caches, project fields)
 * the existing bespoke `usePatchFields` / `usePatchSingleField` pattern in
 * `queries.ts` is still the right tool — don't try to generalize further.
 */
export interface OptimisticListMutationConfig<
  TData,
  TVariables,
  TItem,
  TError = Error,
> {
  /** The actual write (POST/PATCH/DELETE). Must throw on error. */
  mutationFn: (variables: TVariables) => Promise<TData>
  /** Cache key of the list to update optimistically. */
  queryKey: QueryKey
  /**
   * Pure transform applied to the cached list. Receives the current list
   * (always a fresh array, never undefined — pass `[]` when nothing is
   * cached yet) and the mutation variables, returns the next list.
   */
  applyOptimistic: (current: TItem[], variables: TVariables) => TItem[]
  /**
   * Extra query keys to invalidate after the mutation settles
   * (success OR failure). Typical use: invalidate the resource's "all"
   * prefix to catch counts/aggregates rendered from the same cache slice.
   */
  invalidateKeys?: QueryKey[]
  /** Caller hook: success side-effects (toasts, navigation). */
  onSuccess?: (data: TData, variables: TVariables) => void | Promise<void>
  /** Caller hook: error side-effects (toasts). The rollback already ran. */
  onError?: (error: TError, variables: TVariables) => void | Promise<void>
}

interface OptimisticContext<TItem> {
  previous: TItem[] | undefined
}

/**
 * Optimistically update a single list-shaped cache, rolling back on error.
 *
 * Returns the same shape as `useMutation` so callers can pass through
 * `mutate`/`mutateAsync`/`isPending`/etc.
 */
export function useOptimisticListMutation<
  TData,
  TVariables,
  TItem,
  TError = Error,
>(
  config: OptimisticListMutationConfig<TData, TVariables, TItem, TError>,
): UseMutationResult<TData, TError, TVariables, OptimisticContext<TItem>> {
  const qc = useQueryClient()
  const {
    mutationFn,
    queryKey,
    applyOptimistic,
    invalidateKeys = [],
    onSuccess,
    onError,
  } = config

  const options: UseMutationOptions<
    TData,
    TError,
    TVariables,
    OptimisticContext<TItem>
  > = {
    mutationFn,
    onMutate: async (variables) => {
      // Cancel in-flight refetches so they can't overwrite our optimistic
      // value after the server response lands but before invalidation.
      await qc.cancelQueries({ queryKey })
      const previous = qc.getQueryData<TItem[]>(queryKey)
      qc.setQueryData<TItem[]>(queryKey, applyOptimistic(previous ?? [], variables))
      return { previous }
    },
    onError: (err, variables, context) => {
      // Rollback FIRST — caller's onError may toast and we want the UI to
      // be coherent by the time the toast pops.
      if (context?.previous !== undefined) {
        qc.setQueryData<TItem[]>(queryKey, context.previous)
      }
      return onError?.(err, variables)
    },
    onSuccess: (data, variables) => {
      return onSuccess?.(data, variables)
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey })
      for (const key of invalidateKeys) {
        qc.invalidateQueries({ queryKey: key })
      }
    },
  }

  return useMutation(options)
}
